/**
 * SiteModeGate
 *
 * Renders inside AdminAuthProvider so useAdminAuth() works correctly.
 * Uses createPortal so the overlay renders at document.body, bypasses
 * every stacking context in the React tree.
 * Uses get_site_mode() RPC (SECURITY DEFINER) so it works without any
 * RLS SELECT policy on platform_settings.
 *
 * Admin users always bypass the gate.
 */
import { useState, useEffect }  from 'react';
import { createPortal }         from 'react-dom';
import { useAdminAuth }         from '../context/AdminAuthContext';
import { supabase }             from '../lib/supabaseClient';
import ComingSoonPage           from '../pages/ComingSoonPage';
import MaintenancePage          from '../pages/MaintenancePage';

// Pages that always bypass regardless of site mode (auth/admin routes).
const BYPASS = new Set([
  'admin', 'admin-login', 'admin-oauth-callback',
  'coming-soon', 'maintenance',
  'preview', // private preview URLs always bypass site mode gate
  // Editorial + showcase pages are soft-launched ahead of the full site so that
  // the "proof" links inside private reviews (and shared editorial/showcase URLs)
  // resolve for venues even while the homepage/browse stay in coming_soon. These
  // are individual content pages, not the browse surfaces.
  'editorial-article', // /the-edit/{slug}, published editorial features
  'showcase',          // /showcase/{slug} , individual venue showcases
  'vision',            // /vision, positioning page, reachable for investors/venues/media
]);

// Pages a preview prospect is allowed to see after accepting the NDA overlay.
// Deliberately narrow, everything else (homepage, browse, Italy, community…)
// stays gated so prospects can't tour the full site via footer links.
const PREVIEW_ALLOWED = new Set([
  'preview',            // the preview page itself
  'listing-profile',    // venue profile, the "View Venue Profile" destination
  'venue-reviews',      // reviews tab on a venue profile
  'showcase',           // standalone showcase page (if accessed directly)
  'publications-embed', // the flip magazine embedded in the review (Publication opportunity)
]);

export default function SiteModeGate({ page }) {
  const [siteMode, setSiteMode] = useState(null); // null = still loading

  let isAdmin = false;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const auth = useAdminAuth();
    isAdmin = auth?.isAuthenticated ?? false;
  } catch {
    // Rendered outside provider, fail safe, show content
    isAdmin = true;
  }

  // A prospect who accepted the confidentiality overlay has their tab marked as a
  // preview session. Only unlocks PREVIEW_ALLOWED pages, NOT the full site.
  // Footer links to Italy, Browse Venues, community, etc. remain gated.
  //
  // Two sources, both defensive:
  //  1. sessionStorage 'preview_session', set on overlay acceptance. Wrapped in
  //     try/catch because an embedded iframe can be in a partitioned storage
  //     context where reading sessionStorage THROWS (which previously crashed the
  //     gate and left the embedded magazine blank).
  //  2. A ?preview=1 URL marker, used by the magazine embed inside the review,
  //     where the iframe cannot reliably read the parent's sessionStorage. Scoped
  //     to the embed page only.
  const previewBySession = (() => {
    if (typeof window === 'undefined') return false;
    try { return !!sessionStorage.getItem('preview_session'); } catch { return false; }
  })();
  const previewByUrl = (() => {
    if (typeof window === 'undefined') return false;
    try { return new URLSearchParams(window.location.search).get('preview') === '1'; } catch { return false; }
  })();
  const hasPreviewSession = previewBySession || (previewByUrl && page === 'publications-embed');

  useEffect(() => {
    // Use RPC function (SECURITY DEFINER), works regardless of RLS policies
    supabase.rpc('get_site_mode')
      .then(({ data, error }) => {
        if (!error && data) setSiteMode(data);
        else setSiteMode('live'); // fail safe
      })
      .catch(() => setSiteMode('live'));
  }, []);

  // Still loading, don't flash content, but don't block either
  if (siteMode === null) return null;

  // Admins and bypass routes always pass. Live mode passes everything.
  // Preview session passes only the scoped PREVIEW_ALLOWED set.
  if (isAdmin || BYPASS.has(page) || siteMode === 'live') return null;
  if (hasPreviewSession && PREVIEW_ALLOWED.has(page)) return null;

  // Render gate page via portal so it sits at document.body level,
  // completely outside any stacking context from providers/wrappers
  const GatePage = siteMode === 'coming_soon' ? ComingSoonPage
                 : siteMode === 'maintenance'  ? MaintenancePage
                 : null;

  if (!GatePage) return null;

  return createPortal(<GatePage />, document.body);
}
